import React, { ReactNode, useRef, useState } from 'react';
import moment from 'moment';
import { Roles } from '~/enums/Roles';
import { useOnClickOutside } from '~/helpers/helpers';
import { ReactComponent as Ellipsis } from '~/utils/images/ellipsis.svg';
import {
  ReactionType,
  ReactionConfig,
  DEFAULT_REACTIONS
} from '~/types/reaction';
import ReactionPicker from '~/components/ReactionPicker/ReactionPicker';
import ReactionSummary from '~/components/ReactionSummary/ReactionSummary';
import { hiddenByLabel } from '~/helpers/comment';
import { UserMin } from '~/types/user';

// The fields a staff-moderated comment row reads. Place and listing comments
// both carry them; only listing comments have images.
export type StaffComment = {
  uuid: string;
  contents: string;
  images?: { image: string }[];
  hidden: boolean;
  hiddenBy?: string;
  user: UserMin;
  authorName: string;
  createdAt: string;
  myReaction?: string;
  reactionsCount?: number;
  byReactionUserIds?: Record<string, string[]>;
};

interface IProps<T extends StaffComment> {
  comment: T;
  /** What the comment is on (a place, a listing) — rendered next to the author. */
  badge: ReactNode;
  currentUserRole: string;
  isIntegratedBuilding?: boolean;
  selectedCommentId: string;
  setSelectedCommentId: (selectedCommentId: string) => void;
  onHide: (comment: T) => void;
  onNotifyResident: (comment: T) => void;
  onBlockUser?: (comment: T) => void;
  onReact?: (
    commentId: string,
    reactionName: ReactionType | null,
    isRemoving: boolean
  ) => void;
  isReactionLoading?: boolean;
  reactions?: ReactionConfig[];
}

// Staff view of a Place or listing comment on the Comments page. Neither has
// pinning, "great content", or Marketplace moves, so this deliberately renders
// a smaller action set than CommentBox.
const StaffCommentRow = <T extends StaffComment>(props: IProps<T>) => {
  const {
    comment,
    badge,
    currentUserRole,
    isIntegratedBuilding,
    selectedCommentId,
    setSelectedCommentId,
    onHide,
    onNotifyResident,
    onBlockUser,
    onReact,
    isReactionLoading = false,
    reactions = DEFAULT_REACTIONS
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setSelectedCommentId(''));
  const [showReactionPicker, setShowReactionPicker] = useState(false);

  const prettifyTime = (time: string) =>
    moment(time).fromNow().includes('second') === false &&
    moment(time).fromNow().includes('minute') === false &&
    moment(time).fromNow().includes('hour') === false &&
    moment(time).fromNow().includes('day') === false
      ? moment(time).format('ll')
      : moment(time).fromNow();

  const isPMPaid = currentUserRole === Roles.Pm || currentUserRole === Roles.PM;
  const isPMFree = currentUserRole === Roles.PM_FREE;
  const isVCMOrSuperadmin =
    currentUserRole === Roles.Vcm || currentUserRole === Roles.Superadmin;
  const canBlock =
    !!onBlockUser &&
    comment.user?.type === 'resident' &&
    (isVCMOrSuperadmin || ((isPMPaid || isPMFree) && !!isIntegratedBuilding));

  const menuOptions = [
    {
      label: comment.hidden ? 'Unhide' : 'Hide',
      action: () => onHide(comment)
    },
    ...(isPMPaid
      ? [
          {
            label: 'Notify the Resident',
            action: () => onNotifyResident(comment)
          }
        ]
      : []),
    ...(canBlock
      ? [{ label: 'Block user', action: () => onBlockUser?.(comment) }]
      : [])
  ];

  return (
    <div className='comment'>
      <img
        className='avatar margin-right-8'
        alt='avatar'
        src={comment.user.profileImage}
      />
      <div>
        <div className='checkbox-row'>
          <p className='comments-container-txt margin-bottom-4'>
            {comment.authorName ||
              `${comment.user.firstName} ${comment.user.lastName}`}
          </p>
          <p
            className='removed-label checkbox-text margin-left-8'
            style={{
              backgroundColor: '#eef0ff',
              color: '#3b3f8c',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4
            }}
          >
            {badge}
          </p>
          {comment.hidden && (
            <p className='removed-label checkbox-text margin-left-8 warning-background'>
              {`Comment has been HIDDEN by ${hiddenByLabel(comment.hiddenBy)}`}
            </p>
          )}
        </div>
        <p
          className={
            comment.hidden
              ? 'comments-container-content-hidden'
              : 'comments-container-content'
          }
        >
          {comment.contents}
        </p>
        {!!comment.images?.length && (
          <div style={{ overflow: 'auto', marginTop: 8 }}>
            {comment.images.map((commentImage) => (
              <img
                key={commentImage.image}
                src={commentImage.image}
                className={comment.hidden ? 'bw' : ''}
                alt='comment img'
                width='80'
                height='80'
                style={{ borderRadius: 10, marginRight: 8 }}
              />
            ))}
          </div>
        )}
        <p className='comment-time'>{prettifyTime(comment.createdAt)}</p>
        {!comment.hidden && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 4
            }}
          >
            {onReact &&
              (() => {
                const activeConfig = comment.myReaction
                  ? reactions.find((r) => r.type === comment.myReaction)
                  : null;
                const handleReaction = (reaction: ReactionType | null) => {
                  setShowReactionPicker(false);
                  const isRemoving =
                    reaction === null || reaction === comment.myReaction;
                  onReact(
                    comment.uuid,
                    isRemoving ? null : reaction,
                    isRemoving
                  );
                };
                return (
                  <div
                    style={{ position: 'relative', display: 'inline-block' }}
                  >
                    {showReactionPicker && !isReactionLoading && (
                      <ReactionPicker
                        currentReaction={comment.myReaction}
                        onSelectReaction={handleReaction}
                        onClose={() => setShowReactionPicker(false)}
                        reactions={reactions}
                      />
                    )}
                    <button
                      disabled={isReactionLoading}
                      className='comment-reply-btn'
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: 0
                      }}
                      onClick={() => setShowReactionPicker((prev) => !prev)}
                    >
                      {activeConfig ? activeConfig.emoji : 'Like'}
                    </button>
                  </div>
                );
              })()}
            {!!comment.reactionsCount && (
              <ReactionSummary
                reactionsCount={comment.reactionsCount}
                byReactionUserIds={comment.byReactionUserIds || {}}
                reactions={reactions}
              />
            )}
          </div>
        )}
      </div>
      {!!currentUserRole && (isPMPaid || isPMFree || isVCMOrSuperadmin) && (
        <div
          className='ellipsis'
          onClick={() => setSelectedCommentId(comment.uuid)}
        >
          <Ellipsis />
          {selectedCommentId === comment.uuid && (
            <div className='small-list' ref={ref}>
              <div className='more-actions-dropdown'>
                {menuOptions.map((option, index) => (
                  <div
                    key={index}
                    className='more-actions-option pointer'
                    onClick={() => {
                      option.action();
                      setSelectedCommentId('');
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffCommentRow;
